import React, { useCallback, useState } from "react";
import { Modal } from "react-bootstrap";
import { GetFormattedMessage } from "../../../utils/htmlHelper";
import { ExcelTemplate, ExcelTemplateDownload } from "./ExcelTemplate";
import type { UploadProps } from 'antd';
import { Button, message, Upload } from 'antd';
import { serviceUrl } from "../../../config/constants";
import { appLogic, AppProps } from "../../../redux/appLogic";
import { useValues } from 'kea';
import { PageMessages } from "../../../locales/types";
import { RcFile } from "antd/es/upload";

const { Dragger } = Upload;

type Props = {
  showModal: boolean;
  onModalHide: () => void;
  uploadSuccess: () => void;
  organizationId: number;
  title: keyof PageMessages;
};

export const UploadModal = ({
  showModal,
  onModalHide,
  uploadSuccess,
  organizationId,
  title
}: Props): JSX.Element => {
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const { token }: AppProps = useValues(appLogic);

  const uploadSuccessMsg = GetFormattedMessage('app.create_program.upload_success');
  const uploadPartialSuccessMsg = GetFormattedMessage('app.create_program.upload_partial_success');
  const uploadFailMsg = GetFormattedMessage('app.create_program.upload_fail');

  const invalidEmail = GetFormattedMessage('app.create_program.upload_invalid_email');
  const emailNotExist = GetFormattedMessage('app.create_program.upload_email_not_exist');
  const invalidProgram = GetFormattedMessage('app.create_program.upload_invalid_program');
  const invalidGroup = GetFormattedMessage('app.create_program.upload_invalid_group');

  const [loading, setLoading] = useState<boolean>(false);

  const getErrors = useCallback((value: number) => {
    switch (value) {
      case 1:
        return invalidEmail;
      case 2:
        return emailNotExist;
      case 3:
        return invalidProgram;
      case 4:
        return invalidGroup;
    }

    return '';
  }, [emailNotExist, invalidEmail, invalidGroup, invalidProgram])

  const onFileUpload = async (file: RcFile) => {
    try {
      setLoading(true);
      // Step 1: Get Pre-Signed URL
      const response = await fetch(`${serviceUrl}/api/program/GetPresignedUrl?fileName=${file.name}&fileType=${file.type}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to get presigned URL");

      const { uploadUrl, fileUrl } = await response.json();

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
      });

      if (!uploadResponse.ok) throw new Error("Failed to upload file to S3");

      // Step 3: Call backend with file URL
      const batchResponse = await fetch(`${serviceUrl}/api/program/addProgramsBatch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          organizationId: organizationId,
          fileUrl: fileUrl, // Send file URL instead of file itself
        }),
      });

      if (!batchResponse.ok) {
        const responseJson = await batchResponse.json();
        const invalidItens = responseJson.filter((x: any) => x.validations.length > 0);
        const dataSetExample = invalidItens.map((item: any) => ({
          email: item.email,
          program: item.program,
          group: item.group,
          errors: item.validations.map((x: number) => getErrors(x)).join(', ')
        }))

        if (responseJson.filter((x: any) => x.validations.length === 0).length > 0) {
          message.error(<>
            <ExcelTemplateDownload dataSetExample={dataSetExample} />
            {uploadPartialSuccessMsg.replace("{0}", file.name)}
          </>);
        } else {
          message.error(<>
            <ExcelTemplateDownload dataSetExample={dataSetExample} />
            {uploadFailMsg.replace("{0}", file.name)}
          </>);
        }

        setLoading(false);
      } else {
        message.success(uploadSuccessMsg.replace("{0}", file.name));
        uploadSuccess();
        setFileList([]);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      message.error(uploadFailMsg.replace("{0}", file.name));
    }
  };

  // Custom Upload Props to handle process manually
  const props: UploadProps = {
    name: "file",
    multiple: false,
    accept: ".xls,.xlsx",
    maxCount: 1,
    fileList,
    beforeUpload: (file) => {
      setFileList([file]);
      // onFileUpload(file);
      return false; // Prevent default upload behavior
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  return (
    <Modal centered show={showModal} onHide={onModalHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {GetFormattedMessage(title)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <React.Fragment>
          <p>{GetFormattedMessage('app.create.user.download.template.file')}</p>
          <ExcelTemplate />
          <br />
          <Dragger {...props}>
            <p className="ant-upload-text">{GetFormattedMessage('app.create.user.drag.n.drop.text')}</p>
            <p className="ant-upload-hint">
              {GetFormattedMessage('app.create.user.dran.n.drop.available.files')}
            </p>
          </Dragger>
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
            <Button loading={loading} type="primary" disabled={fileList.length === 0} onClick={() => onFileUpload(fileList[0])}>Save</Button>
          </div>
        </React.Fragment>
      </Modal.Body>
    </Modal>
  );
};
